import { useCallback, useContext, useEffect, useMemo } from 'react';
import Categories from 'ContentUtils/constants/Categories';
import ContentUsageLimitsContext from 'ContentEditorUI/context/ContentUsageLimitsContext';
import useFetchContentUsageLimits from 'ContentEditorUI/hooks/useFetchContentUsageLimits';
import { jsx as _jsx } from "react/jsx-runtime";
const getStateKeyPrefix = contentTypeId => {
  switch (contentTypeId) {
    case Categories.blog_post.id:
      return 'blogPosts';
    case Categories.landing_page.id:
      return 'landingPages';
    case Categories.site_page.id:
    default:
      return 'sitePages';
  }
};
const getLimitInfoForBlogPosts = (isOverBlogLimit, hasReachedBlogPostsLimit) => {
  const cannotCreateMoreContent = isOverBlogLimit || hasReachedBlogPostsLimit;
  if (cannotCreateMoreContent) {
    const disabledTooltipSuffix = isOverBlogLimit && hasReachedBlogPostsLimit ? 'bothBlogAndBlogPostsLimitReached' : isOverBlogLimit ? 'blogLimitReached' : 'blogPostsLimitReached';
    return {
      cannotCreateMoreContent,
      disabledTooltipSuffix
    };
  }
  return {
    cannotCreateMoreContent: false,
    disabledTooltipSuffix: ''
  };
};
const ContentUsageLimitsProvider = ({
  children
}) => {
  const {
    fetchContentUsage,
    contentUsageLimits
  } = useFetchContentUsageLimits();
  useEffect(() => {
    fetchContentUsage().catch(() => {});
  }, [fetchContentUsage]);
  const getUsageLimitInfoForContentTypeId = useCallback(contentTypeId => {
    const stateKeyPrefix = getStateKeyPrefix(contentTypeId);
    const {
      quantityUsed,
      limit
    } = contentUsageLimits[stateKeyPrefix];
    const hasReachedContentLimit = limit ? quantityUsed >= limit : false;
    if (contentTypeId === Categories.blog_post.id) {
      const {
        quantityUsed: blogQuantityUsed,
        limit: blogLimit
      } = contentUsageLimits.blog;
      const isOverBlogLimit = blogLimit ? blogQuantityUsed > blogLimit : false;
      return getLimitInfoForBlogPosts(isOverBlogLimit, hasReachedContentLimit);
    }
    const disabledTooltipSuffix = contentTypeId === Categories.landing_page.id ? 'landingPagesLimitReached' : 'sitePagesLimitReached';
    return {
      cannotCreateMoreContent: hasReachedContentLimit,
      disabledTooltipSuffix: hasReachedContentLimit ? disabledTooltipSuffix : ''
    };
  }, [contentUsageLimits]);
  const value = useMemo(() => ({
    getUsageLimitInfoForContentTypeId,
    contentUsageLimits
  }), [getUsageLimitInfoForContentTypeId, contentUsageLimits]);
  return /*#__PURE__*/_jsx(ContentUsageLimitsContext.Provider, {
    value: value,
    children: children
  });
};
export default ContentUsageLimitsProvider;
export function useContentUsageLimits() {
  const context = useContext(ContentUsageLimitsContext);
  if (context === undefined) {
    throw new Error('useContentUsageLimits must be used within a ContentUsageLimitsProvider');
  }
  return context;
}